import { HubConnection, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { LoadingButton } from "@mui/lab";
import { Box, CircularProgress, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import AppTextInput from "../../../app/components/AppTextInput";
import { ChatComment } from "../../../app/models/comment";
import { useAppDispatch, useAppSelector } from "../../../app/store/configureStore";
import { yupResolver } from '@hookform/resolvers/yup';
import { helpInOrderCommentsSelectors, setComments, setSelectedOrder, updateMessage } from "./adminHelpInOrderCommentsSlice";
import { adminHelpInOrderValidationSchema } from "./adminOrderValidation";
import AppDisplayComments from "../../../app/components/AppDisplayComments";

interface Props {
    orderId: number;
}

interface FormData {
    body: string;
}

export default function AdminHelpInOrderChat({ orderId }: Props) {
    const token = useAppSelector(state => state.account.user?.token);
    const userEmail = useAppSelector(state => state.account.user?.email);
    const dispatch = useAppDispatch();
    const comments = useAppSelector(helpInOrderCommentsSelectors.selectAll);
    const [connection, setConnection] = useState<HubConnection | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const baseUrl = import.meta.env.VITE_URL || 'http://localhost:7200/';
        const newConnection = new HubConnectionBuilder()
            .withUrl(`${baseUrl}chat?orderId=` + orderId, {
                accessTokenFactory: () => token!
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();

        setLoading(true);
        newConnection.start().catch(error => console.log('Wystąpił problem z nawiązaniem połączenia: ', error));

        newConnection.on('LoadComments', (comments: ChatComment[]) => {
            dispatch(setSelectedOrder(orderId));
            dispatch(setComments(comments));
            setLoading(false);
        });

        newConnection.on("ReceiveComment", (comment: ChatComment) => {
            console.log(comment);
            dispatch(updateMessage(comment));
        });

        setConnection(newConnection);

        return () => {
            newConnection.stop();
        };
    }, [dispatch, orderId, token]);

    const handleSendComment = async (data: any) => {
        const comment = {
            body: data.body,
            orderId: orderId,
            userEmail: userEmail
        } as ChatComment;

        if (connection) {
            try {
                await connection.invoke('SendComment', comment);
                reset({ body: '' });
            } catch (error) {
                console.log(error);
            }
        } else {
            console.log('Połączenie nie zostało zainicjowane');
        }
    };

    const { control, handleSubmit, reset, formState: { isSubmitting, isValid }, formState: { errors } } = useForm<FormData>({
        mode: 'all',
        resolver: yupResolver(adminHelpInOrderValidationSchema)
    });

    return (
        <>
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : <AppDisplayComments comments={comments} />
            }

            <Grid item xs={12} sx={{ mt: 2 }}>
                <Box
                    component="form"
                    onSubmit={handleSubmit(handleSendComment)}
                    noValidate
                >
                    <div>
                        <AppTextInput control={control} name="body" label="Dodaj wiadomość" multiline rows={3} error={!!errors.body} helperText={errors?.body?.message as string} />
                    </div>
                    <LoadingButton
                        disabled={!isValid || isSubmitting}
                        loading={isSubmitting}
                        type="submit"
                        //fullWidth
                        variant="outlined"
                        sx={{ mt: 3, mb: 2 }}
                    >
                        Dodaj wiadomość
                    </LoadingButton>
                </Box>
            </Grid>
        </>
    )
}